import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { CbmImpl, ParticipantLevel } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { checkReservationExists, getReservationsByTask, isEmailTask } from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface AddEmailParticipantOptions {
    name: string;
}






























export class AddEmailParticipant implements Action<Promise<void>> {
    readonly #taskSid: string;

    readonly #email: string;

    readonly #level: ParticipantLevel;

    readonly #options?: AddEmailParticipantOptions;

    constructor(taskSid: string, email: string, level: ParticipantLevel, options?: AddEmailParticipantOptions) {
        this.#taskSid = taskSid;
        this.#email = email;
        this.#level = level;
        this.#options = options;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const reservation = getReservationsByTask(worker, this.#taskSid)[0];

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "AddEmailParticipant",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.AddEmailParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                participantLevel: this.#level
            },
            logger
        });

        const task = reservation.task;

        if (!isEmailTask(task)) {
            const errorMsg = `AddEmailParticipant: Task ${this.#taskSid} is not an email task`;
            sendWorkerTrackingEvent(EVENTS.AddEmailParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantLevel: this.#level
            });

            logger.error(errorMsg);
            return Promise.reject(
                createFlexSdkError(context)(ErrorCode.TaskNotEmailTask, { severity: ErrorSeverity.Error }, errorMsg)
            );
        }

        try {
            const participant = await cbm.addEmailParticipant(task, {
                address: this.#email,
                level: this.#level,
                toName: this.#options?.name ?? ""
            });
            sendWorkerTrackingEvent(EVENTS.AddEmailParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                participantLevel: this.#level,
                participantSid: participant.participantSid
            });
            return Promise.resolve();
        } catch (error) {
            const errorMsg = `AddEmailParticipant: Failed to add participant to task ${this.#taskSid}`;
            sendWorkerTrackingEvent(EVENTS.AddEmailParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantLevel: this.#level
            });
            logger.error(errorMsg, error);
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToAddEmailParticipant,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
